import React, { useCallback } from "react";
import { GoogleMap, useLoadScript, Marker } from "@react-google-maps/api";

interface MiniMapProps {
  latitude: number;
  longitude: number;
  address?: string;
  darkMode?: boolean;
}

const containerStyle = {
  width: "100%",
  height: "200px",
  borderRadius: "8px",
};

const MiniMap: React.FC<MiniMapProps> = ({
  latitude,
  longitude,
  address,
  darkMode = false,
}) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
  });

  const center = {
    lat: latitude,
    lng: longitude,
  };

  const mapOptions: google.maps.MapOptions = {
    disableDefaultUI: true,
    zoomControl: true,
    scrollwheel: false,
    draggable: false,
    styles: darkMode
      ? [
          { elementType: "geometry", stylers: [{ color: "#242f3e" }] },
          { elementType: "labels.text.stroke", stylers: [{ color: "#242f3e" }] },
          { elementType: "labels.text.fill", stylers: [{ color: "#746855" }] },
          {
            featureType: "administrative.locality",
            elementType: "labels.text.fill",
            stylers: [{ color: "#d59563" }],
          },
          {
            featureType: "road",
            elementType: "geometry",
            stylers: [{ color: "#38414e" }],
          },
          {
            featureType: "road",
            elementType: "geometry.stroke",
            stylers: [{ color: "#212a37" }],
          },
          {
            featureType: "road",
            elementType: "labels.text.fill",
            stylers: [{ color: "#9ca5b3" }],
          },
          {
            featureType: "water",
            elementType: "geometry",
            stylers: [{ color: "#17263c" }],
          },
        ]
      : undefined,
  };

  const handleMapClick = useCallback(() => {
    // Open Google Maps directions in new tab
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
    window.open(mapsUrl, "_blank");
  }, [latitude, longitude]);

  if (loadError) {
    return (
      <div
        style={{
          ...containerStyle,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: darkMode ? "#333" : "#f5f5f5",
          color: darkMode ? "#fff" : "#666",
        }}
      >
        Error loading map
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div
        style={{
          ...containerStyle,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: darkMode ? "#333" : "#f5f5f5",
          color: darkMode ? "#fff" : "#666",
        }}
      >
        Loading map...
      </div>
    );
  }

  return (
    <div>
      <div
        style={{ cursor: "pointer", borderRadius: "8px", overflow: "hidden" }}
        onClick={handleMapClick}
        title="Click to open in Google Maps"
      >
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={center}
          zoom={12}
          options={mapOptions}
        >
          <Marker position={center} />
        </GoogleMap>
      </div>
      <div
        style={{
          backgroundColor: darkMode ? "#1a1a1a" : "#f0f0f0",
          color: darkMode ? "#fff" : "#333",
          padding: "10px 12px",
          borderRadius: "0 0 8px 8px",
          marginTop: "-4px",
        }}
      >
        <div style={{ fontWeight: 500 }}>
          {address || `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`}
        </div>
        <div style={{ fontSize: "12px", color: darkMode ? "#aaa" : "#666", marginTop: "2px" }}>
          Location is approximate
        </div>
      </div>
    </div>
  );
};

export default MiniMap;
